import { useParams } from "react-router";
import styles from "./PhotoViewer.module.scss";
import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { DeleteModal } from "./AppModal";
import useToast from "../util/useToast";
import { Dropdown } from "react-bootstrap";

export const PhotoViewer = ({ photos, photo, setPhoto, reloadPhotos, publicPath, onDelete }) => {
  const { showToastSuccess } = useToast();

  const photoContainerRef = useRef(null);

  const [dimension, setDimension] = useState(null);
  const [dimensionValue, setDimensionValue] = useState(null);

  const [deletePhotoModal, setDeletePhotoModal] = useState({ show: false });

  const currentIndex = photo ? photos.findIndex(p => p.id === photo.id) : 0;

  const goLeft = useCallback(() => {
    if (currentIndex > 0) {
      setPhoto(photos[currentIndex - 1]);
    } else {
      setPhoto(photos[photos.length - 1]);
    }
  }, [currentIndex, photos, setPhoto]);

  const goRight = useCallback(() => {
    if (currentIndex < photos.length - 1) {
      setPhoto(photos[currentIndex + 1]);
    } else {
      setPhoto(photos[0]);
    }
  }, [currentIndex, setPhoto, photos]);

  useEffect(() => {
    if (!photo) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setPhoto(null);
      } else if (event.key === "ArrowLeft") {
        goLeft();
      } else if (event.key === "ArrowRight") {
        goRight();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [setPhoto, photo, goLeft, goRight]);

  useEffect(() => {
    if (photoContainerRef.current && photo) {
      const containerWidth = photoContainerRef.current.offsetWidth;
      const containerHeight = photoContainerRef.current.offsetHeight;

      const pictureAspectRatio = photo.pictureWidth / photo.pictureHeight;
      const containerAspectRatio = containerWidth / containerHeight;

      const dpr = window.devicePixelRatio || 1;

      if (pictureAspectRatio > containerAspectRatio) {
        // Picture is wider: width is the limiting factor
        setDimensionValue(Math.ceil((containerWidth * dpr) / 200) * 200);
        setDimension("w");
      } else {
        // Picture is taller: height is the limiting factor
        setDimensionValue(Math.ceil((containerHeight * dpr) / 200) * 200);
        setDimension("h");
      }
    }
  }, [photo]);

  const deletePhoto = () => {
    const closeAfterDeletion = photos.length === 1;

    return onDelete(photo.id)
      .then(reloadPhotos)
      .then(() => closeAfterDeletion ? setPhoto(null) : goRight())
      .then(() => {
        setDeletePhotoModal({ show: false });

        showToastSuccess("Photo supprimée");
      });
  };

  if (!photo) {
    return null;
  }

  return <div className={clsx(styles.photoViewer)}>
    <div className={clsx(styles.action, styles.close)} onClick={() => setPhoto(null)}>
      <span className="material-icons">close</span>
    </div>
    <div className={clsx(styles.action, styles.left)} onClick={goLeft}>
      <span className="material-icons">chevron_left</span>
    </div>
    <div className={clsx(styles.action, styles.right)} onClick={goRight}>
      <span className="material-icons">chevron_right</span>
    </div>

    {onDelete && <Dropdown align="end">
      <Dropdown.Toggle id="dropdown-custom-components" as={CustomToggle}>
        <div className={clsx(styles.action, styles.options)} onClick={() => setPhoto(null)}>
          <span className="material-icons">more_vert</span>
        </div>
      </Dropdown.Toggle>

      <Dropdown.Menu>
        <Dropdown.Item className={styles.option} onClick={() => setDeletePhotoModal({ show: true })}>
          <span className={clsx(styles.optionIcon, "material-icons")}>delete</span>
          Supprimer
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>}

    <div className={styles.container}>
      <div className={styles.photoContainer} ref={photoContainerRef}>
        {dimensionValue && <img
          src={process.env.REACT_APP_API_URL + "/public/" + publicPath + "/" + photo.fileKey + "?" + dimension + "=" + dimensionValue}
          alt={photo.name} />}
      </div>

      <div className={styles.photoDetails}>
        details
      </div>
    </div>

    <DeleteModal
      size={"sm"}
      show={deletePhotoModal.show}
      title="Supprimer la photo"
      onCancel={() => setDeletePhotoModal({ show: false })}
      onSubmit={deletePhoto}
      cancelLabel="Annuler"
      submitLabel="Supprimer"
    >
      Etes-vous sûr de vouloir supprimer cette photo ?
    </DeleteModal>
  </div>;
};

const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
  <div className={clsx(styles.action, styles.options)} ref={ref} onClick={(e) => {
    e.preventDefault();
    onClick(e);
  }}><span
    className="material-icons">more_vert</span>
  </div>
));